import React from "react";
import classNames from "classnames";
import Link from "next/link";

import { Action, Category } from "@/constants/events";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getGeoSlug, getLocationState } from "@/redux/selectors/location";
import { navLinkRetailerUrlBuilder } from "@/utils/navLinkRetailerUrlBuilder";

export const HeaderLinks: React.FC<{
  className?: string;
  showHeaderTest?: boolean;
  navMobileSplitMetricTrackingClick?: (
    navLink: "delivery" | "dispensaries",
    feature: "header-nav" | "hamburger",
  ) => void;
}> = ({ showHeaderTest, navMobileSplitMetricTrackingClick, className }) => {
  const userLocation = useSelector(getLocationState);

  const countryCode = useDomainCountryCode();

  const { publishEvent } = useEventTracker();

  const buildNavigationLinkUrl = (directory: string) =>
    navLinkRetailerUrlBuilder(userLocation, directory);

  const trackLinkClick = (eventLabel: string) => {
    publishEvent({
      action: Action.click,
      category: Category.header,
      label: eventLabel,
    });
  };

  const linkClasses = `mr-md mt-md font-bold whitespace-nowrap hover:underline`;
  const geoSlug = useSelector(getGeoSlug);
  return (
    <nav
      className={classNames("overflow-x-auto whitespace-nowrap", className)}
      id="global-header-nav-links"
      data-testid="global-header-nav-links"
      aria-label={showHeaderTest ? "Popular pages on leafly" : "Popular pages"}
    >
      <Link
        className={linkClasses}
        data-testid="shop"
        href={`/shop${geoSlug ? `?location=${geoSlug}` : ""}`}
        onClick={() => {
          trackLinkClick("shop");
        }}
      >
        Shop
      </Link>

      <Link
        className={linkClasses}
        data-testid="delivery"
        href="/delivery"
        onClick={() => {
          trackLinkClick("delivery");
          navMobileSplitMetricTrackingClick?.("delivery", "header-nav");
        }}
      >
        Delivery
      </Link>

      <Link
        className={linkClasses}
        data-testid="dispensaries"
        href={buildNavigationLinkUrl("dispensaries")}
        onClick={() => {
          trackLinkClick("dispensaries");
          navMobileSplitMetricTrackingClick?.("dispensaries", "header-nav");
        }}
      >
        {countryCode === "CA" ? "Stores" : "Dispensaries"}
      </Link>

      <Link
        className={linkClasses}
        data-testid="deals"
        href="/deals"
        onClick={() => {
          trackLinkClick("deals");
        }}
      >
        Deals
      </Link>

      <Link
        className={linkClasses}
        data-testid="strains"
        href="/strains/lists"
        onClick={() => {
          trackLinkClick("strains");
        }}
      >
        Strains
      </Link>

      <Link
        className={linkClasses}
        data-testid="brands"
        href="/brands"
        onClick={() => {
          trackLinkClick("brands");
        }}
      >
        Brands
      </Link>

      <Link
        className={linkClasses}
        data-testid="products"
        href="/products"
        onClick={() => {
          trackLinkClick("products");
        }}
      >
        Products
      </Link>

      {countryCode !== "CA" && (
        <Link
          className={linkClasses}
          data-testid="cbd"
          href={buildNavigationLinkUrl("cbd-stores")}
          onClick={() => {
            trackLinkClick("cbd");
          }}
        >
          CBD
        </Link>
      )}

      <Link
        className={linkClasses}
        data-testid="doctors"
        href={buildNavigationLinkUrl("medical-marijuana-doctors")}
        onClick={() => {
          trackLinkClick("doctors");
        }}
      >
        Doctors
      </Link>

      <Link
        className={linkClasses}
        data-testid="cannabis-101"
        href="/news/cannabis-101"
        onClick={() => {
          trackLinkClick("cannabis 101");
        }}
      >
        Cannabis 101
      </Link>

      <Link
        className={linkClasses}
        data-testid="social-impact"
        href="/social-impact"
        onClick={() => {
          trackLinkClick("social impact");
        }}
      >
        Social impact
      </Link>
    </nav>
  );
};
